/**
 * The task map, laid out. A pure function from a stored step graph to coordinates.
 *
 * WHY THERE IS NO DIAGRAM LIBRARY BEHIND THIS. The obvious build is Vue Flow plus dagre,
 * and it was rejected for three reasons that all point the same way:
 *
 *   • It would have to be added to `package.json`, which is an UPSTREAM file. Every entry
 *     we put there is a line that conflicts on the next `git merge upstream/<tag>`, and the
 *     one rule of this fork is that customisation is additive and lives where upstream
 *     cannot reach it. A dependency is the one kind of customisation that cannot.
 *   • Vue Flow ships its own CSS, its own palette and its own dark-mode strategy — a second
 *     theme engine, which is the exact complaint `TrendChart.vue` was written to avoid, and
 *     which `waslo-lint.mjs` cannot audit because the colours live in a stylesheet inside a
 *     package rather than in an `n-*` token in our own source.
 *   • It buys interaction we are not allowed to ship. The map is READ-ONLY by decision
 *     (spec §4, and "a draggable canvas" is listed under Out of scope), so a pan-and-zoom
 *     canvas with drag handles would be about 70 KB of code whose entire purpose is the
 *     feature we are not building.
 *
 * What is actually needed is a layering function and some line segments, and that is what
 * this file is. The component renders inline SVG coloured by the same `n-*` classes as
 * everything else in the section, so the map is themed, CSP-clean and lint-covered for free.
 *
 * WHY IT IS A SEPARATE FILE FROM THE COMPONENT. Layering a cyclic graph has more edge cases
 * than a mounted-component test can comfortably reach — a loop back to an earlier step, a
 * step nothing links to, a link to a step that was deleted, a definition written by a newer
 * sidecar carrying a step type this build has never heard of. Each of those is one call and
 * one `expect` here, and would be a mount, a render and a DOM query over there.
 *
 * WHAT IT IS FAITHFUL TO. The links it draws are the ones the ENGINE will follow, which is
 * not quite the same set `validate.ts#outgoing` reports:
 *
 *   • A `choose` option with no `next` of its own falls back to the step's `next`
 *     (`decide.ts#branchOf`), so the map draws that fallback as the option's own arrow
 *     rather than as a second, unexplained line leaving the step.
 *   • A `call` leaves by `onWorked ?? next` when it worked, and by `onFailed` when it did
 *     not — with no `onFailed` it hands off, and there is no step to point at.
 *   • `end` and `handoff` are terminal in `decide.ts` whatever `next` says, so nothing is
 *     drawn leaving them. `validate.ts` still reads their `next` when it hunts for dangling
 *     links, and it is right to: the map showing a link the engine ignores would be worse
 *     than the owner never seeing it.
 *
 * Nothing here throws. It is handed whatever the API returned, including `null`.
 */

// ── the drawing, in SVG user units ─────────────────────────────────────────────────
// One unit is one CSS pixel: the map renders at its natural size inside a horizontal
// scroller rather than being squeezed to the column width, because a 16-step graph scaled
// to fit a phone is a picture of a graph rather than a readable one.

/** Wide enough for two lines of a step name at 22 characters, at the type size below. */
export const BOX_W = 176;
export const BOX_H = 72;
/** Between two boxes on the same row. */
const GAP_X = 28;
/** Between rows — the elbow of every link that changes column lives in here. */
const GAP_Y = 60;
/** Between two routing lanes at the side, and between the outermost lane and the boxes. */
const LANE_GAP = 26;
const PAD = 16;
/** Room above the first row for the "starts here" line, which sits outside its box. */
const PAD_TOP = 40;
/** Room below the last row for a line leaving it on its way to a lane. */
const PAD_BOTTOM = 32;
const ARROW = 7;
/**
 * THE GUTTER BETWEEN TWO ROWS, BUDGETED. Everything horizontal happens in here, and the
 * whole reason these are four separate numbers is that the first version let them collide:
 * `GAP_Y / 2` put an elbow's turn at exactly the midpoint of the straight link beside it, so
 * the one word naming a branch ("Book") was rendered with a line through it — and a loop
 * leaving one step ran at the same height as the branch words of the step next to it.
 *
 * Measured from the bottom edge of the row above, top to bottom:
 *
 *   4   a lane link's label, which belongs to the step it leaves           LANE_TURN_OUT - 5
 *   14  a lane link turning out towards its lane                           LANE_TURN_OUT
 *   29  an elbow's label, sitting on its own horizontal run                TURN_Y - 5
 *   26  a straight link's label (a different column, so it cannot clash)   STRAIGHT_LABEL
 *   34  an elbow's horizontal run                                          TURN_Y
 *   44  a lane link turning in, measured from the row BELOW                GAP_Y - LANE_TURN_IN
 */
const LANE_TURN_OUT = 14;
const LANE_TURN_IN = 16;
const TURN_Y = 34;
const STRAIGHT_LABEL = 26;

/** Where a step's own text sits inside its box. */
export const TEXT_Y = { type: 18, name: [36, 50], badge: 65 };

const STEP_TYPES = new Set([
  'ask',
  'choose',
  'choose_live',
  'call',
  'tell',
  'handoff',
  'end',
]);

/**
 * Steps the engine never leaves. `next` on one of these is dead data — see the file
 * header — and drawing it would send an owner looking for a branch that cannot run.
 */
const TERMINAL_TYPES = new Set(['end', 'handoff']);

const NAME_LINE_CHARS = 22;
const MAX_NAME_LINES = 2;
/** An option label is capped at 20 characters upstream; this is what fits beside a line. */
const OPTION_LABEL_CHARS = 16;
/** How many branch labels one merged arrow prints before it starts counting instead. */
const MAX_LABEL_PARTS = 2;

const isRecord = value =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** A non-empty trimmed string, or `undefined`. Anything else in the data is not a link. */
const id = value => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

const truncate = (text, max) =>
  text.length <= max ? text : `${text.slice(0, max - 1)}…`;

/**
 * A step name across at most two lines, breaking on spaces where it can.
 *
 * Word-aware rather than a hard slice because the names owners write are short phrases
 * ("Which day suits you", "أي يوم يناسبك") and slicing mid-word makes a two-line box read
 * as a bug. A word longer than the line is cut, because the alternative is a box that
 * silently shows nothing.
 */
export const wrapName = (name, lineChars = NAME_LINE_CHARS) => {
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];
  const lines = [];
  let line = '';
  words.forEach(word => {
    const candidate = line === '' ? word : `${line} ${word}`;
    if (candidate.length <= lineChars) {
      line = candidate;
      return;
    }
    if (line !== '') lines.push(line);
    line = word.length <= lineChars ? word : word.slice(0, lineChars);
  });
  if (line !== '') lines.push(line);
  if (lines.length <= MAX_NAME_LINES) return lines;
  // An ellipsis is forced onto the last line we keep rather than left to `truncate`, which
  // only adds one when a line is over length: a name cut at a word boundary fits perfectly
  // and would have been shown as though it were the whole name. The full name is still on
  // the box's tooltip and in the list underneath, but the box has to admit it is shortened.
  const kept = lines.slice(0, MAX_NAME_LINES);
  const last = kept[MAX_NAME_LINES - 1];
  kept[MAX_NAME_LINES - 1] =
    last.length >= lineChars ? `${last.slice(0, lineChars - 1)}…` : `${last}…`;
  return kept;
};

/**
 * The label an owner reads on a `choose` option's arrow.
 *
 * The Arabic label is preferred on an Arabic dashboard and the English one is the fallback
 * — not the other way round, and not "whichever is filled in first". A half-translated task
 * is the normal state of a task being written, and an owner reading the Arabic map needs to
 * see which branch is still missing its Arabic rather than a map that silently looks done.
 */
const optionLabel = (option, lang) => {
  const ar = typeof option.labelAr === 'string' ? option.labelAr.trim() : '';
  const en = typeof option.labelEn === 'string' ? option.labelEn.trim() : '';
  const preferred = lang === 'ar' ? ar || en : en || ar;
  return truncate(preferred, OPTION_LABEL_CHARS);
};

/**
 * Every step this one hands control to, as the ENGINE hands it — see the file header for
 * where this deliberately differs from `validate.ts#outgoing`.
 *
 * `kind` is a token the component translates; only `option` carries text, and that text is
 * the owner's own data rather than a phrase of ours.
 */
export const branchesOf = (step, lang = 'en') => {
  if (TERMINAL_TYPES.has(step.type)) return [];
  const raw = step.raw;
  const next = id(raw.next);

  if (step.type === 'choose') {
    const options = Array.isArray(raw.options) ? raw.options : [];
    const branches = [];
    options.forEach(option => {
      if (!isRecord(option)) return;
      const to = id(option.next) ?? next;
      if (to === undefined) return;
      branches.push({ to, kind: 'option', label: optionLabel(option, lang) });
    });
    // A choose with no options at all is a half-written step, not a step with no way out.
    if (branches.length === 0 && next !== undefined) {
      return [{ to: next, kind: 'then', label: '' }];
    }
    return branches;
  }

  if (step.type === 'choose_live') {
    const branches = [];
    if (next !== undefined)
      branches.push({ to: next, kind: 'then', label: '' });
    const ifNone = id(raw.ifNone);
    if (ifNone !== undefined) {
      branches.push({ to: ifNone, kind: 'none', label: '' });
    }
    return branches;
  }

  if (step.type === 'call') {
    const branches = [];
    const worked = id(raw.onWorked) ?? next;
    if (worked !== undefined) {
      branches.push({ to: worked, kind: 'worked', label: '' });
    }
    const failed = id(raw.onFailed);
    if (failed !== undefined) {
      branches.push({ to: failed, kind: 'failed', label: '' });
    }
    return branches;
  }

  return next === undefined ? [] : [{ to: next, kind: 'then', label: '' }];
};

/**
 * The steps, de-duplicated and defended.
 *
 * An unknown `type` is kept rather than dropped: a definition written by a newer sidecar
 * must survive an older builder (the graph contract says so for writes, and a map that
 * silently omits a step is the read-side version of the same bug). It renders as a step
 * with no type word and no branches, which is honest — this build does not know where it
 * goes.
 */
const readSteps = definition => {
  const raw = Array.isArray(definition?.steps) ? definition.steps : [];
  const seen = new Set();
  const steps = [];
  raw.forEach(entry => {
    if (!isRecord(entry)) return;
    const stepId = id(entry.id);
    if (stepId === undefined || seen.has(stepId)) return;
    seen.add(stepId);
    const name = typeof entry.name === 'string' ? entry.name.trim() : '';
    steps.push({
      id: stepId,
      type: STEP_TYPES.has(entry.type) ? entry.type : 'unknown',
      // A step with no name is identified by the id the owner gave it, which is the only
      // other thing on the row she will recognise.
      name: name === '' ? stepId : name,
      raw: entry,
    });
  });
  return steps;
};

/**
 * Links classified into "goes forward" and "loops back".
 *
 * Depth-first from the first step, then from anything it could not reach, marking every
 * link that points at a step still open on the stack. Removing exactly those leaves a graph
 * with no cycles — which is what lets the layering below be a plain longest-path walk, and
 * it is why the Seamless task's two loop-backs (a day with no free times, and "change
 * something") cannot spin the layout.
 *
 * Iterative, not recursive: a task is capped at 30 steps so the stack depth is never a
 * problem in practice, but a recursive DFS over data from the network is a stack overflow
 * waiting for the one definition nobody tested.
 */
const classifyLinks = (steps, links) => {
  const outgoing = new Map(steps.map(step => [step.id, []]));
  links.forEach(link => outgoing.get(link.from)?.push(link));

  const state = new Map();
  const visitOrder = new Map();
  let counter = 0;

  const walk = root => {
    if (state.has(root)) return;
    state.set(root, 'open');
    visitOrder.set(root, counter);
    counter += 1;
    const stack = [{ id: root, at: 0 }];
    while (stack.length > 0) {
      const frame = stack[stack.length - 1];
      const out = outgoing.get(frame.id) ?? [];
      if (frame.at >= out.length) {
        state.set(frame.id, 'done');
        stack.pop();
      } else {
        const link = out[frame.at];
        frame.at += 1;
        const seen = state.get(link.to);
        // Open means it is an ancestor of this link on the current path — including the
        // step itself, which is how a step that loops straight back to itself is caught.
        // Done means it is finished with, so the link goes forward across the graph.
        if (seen === 'open') {
          link.isBack = true;
        } else if (seen !== 'done') {
          state.set(link.to, 'open');
          visitOrder.set(link.to, counter);
          counter += 1;
          stack.push({ id: link.to, at: 0 });
        }
      }
    }
  };

  return { walk, visitOrder };
};

/**
 * One row per step, by longest path from wherever it can first be entered.
 *
 * Longest rather than shortest so a step never sits above something that must run before
 * it: with shortest-path ranking a confirmation reached both directly and through three
 * questions would be drawn beside the first question, with an arrow running back up the
 * page for a branch that only ever goes forward.
 */
const rankSteps = (steps, forwardLinks) => {
  const rank = new Map(steps.map(step => [step.id, 0]));
  const indegree = new Map(steps.map(step => [step.id, 0]));
  const out = new Map(steps.map(step => [step.id, []]));
  forwardLinks.forEach(link => {
    out.get(link.from)?.push(link.to);
    indegree.set(link.to, (indegree.get(link.to) ?? 0) + 1);
  });

  const queue = steps
    .filter(step => indegree.get(step.id) === 0)
    .map(s => s.id);
  while (queue.length > 0) {
    const current = queue.shift();
    (out.get(current) ?? []).forEach(to => {
      rank.set(to, Math.max(rank.get(to) ?? 0, (rank.get(current) ?? 0) + 1));
      const left = (indegree.get(to) ?? 0) - 1;
      indegree.set(to, left);
      if (left === 0) queue.push(to);
    });
  }
  return rank;
};

/**
 * One box, one colour.
 *
 * Ordered by how much it costs the owner NOT to notice: a link to a step that is gone breaks
 * the task on the next message, a step nothing reaches can never run at all, and a step
 * nothing follows stops the task early. Being the first step is the only one of the four
 * that is not a problem, so it is the one that gives way.
 */
const toneOf = ({ isBroken, isOrphan, isDeadEnd, isFirst }) => {
  if (isBroken) return 'broken';
  if (isOrphan) return 'orphan';
  if (isDeadEnd) return 'deadEnd';
  return isFirst ? 'first' : 'plain';
};

/** Which steps the engine can actually arrive at, breadth-first from the first step. */
const reachableFrom = (firstStepId, steps, links) => {
  const out = new Map(steps.map(step => [step.id, []]));
  links.forEach(link => out.get(link.from)?.push(link.to));
  const reached = new Set();
  const queue = out.has(firstStepId) ? [firstStepId] : [];
  while (queue.length > 0) {
    const current = queue.shift();
    if (!reached.has(current)) {
      reached.add(current);
      (out.get(current) ?? []).forEach(to => queue.push(to));
    }
  }
  return reached;
};

/**
 * The three points of a filled arrowhead pointing down, with its tip at `(x, y)`.
 *
 * Down is the only direction there is, because every link — elbow, skip ahead or loop back —
 * arrives at the top edge of the step it points at. That is not a simplification of the
 * drawing, it IS the drawing: an arrow always entering from above is how a loop back to an
 * earlier question still reads as "and then it asks this again".
 *
 * Drawn as a polygon rather than an SVG `marker`, so its colour is an ordinary `n-*` class
 * on an ordinary element. A marker's fill does not inherit from the path that uses it
 * without `context-stroke`, which is not something to rely on across browsers, and the
 * fallback is a hard-coded colour — which `waslo-lint.mjs` R1 would rightly refuse.
 */
const arrowPoints = (x, y) =>
  [
    [x, y],
    [x - 5, y - ARROW],
    [x + 5, y - ARROW],
  ]
    .map(([px, py]) => `${px},${py}`)
    .join(' ');

/**
 * The map.
 *
 * @param {object|null} definition  `{ firstStepId, steps }` exactly as the API returned it.
 * @param {{ lang?: 'en'|'ar' }} [options]  Which side of a bilingual option label to print.
 * @returns {{
 *   isEmpty: boolean, width: number, height: number, stepCount: number,
 *   firstStepId: string, hasFirstStep: boolean,
 *   steps: object[], links: object[], flagged: object[]
 * }}
 */
export const taskMapLayout = (definition, options = {}) => {
  const lang = options.lang === 'ar' ? 'ar' : 'en';
  const steps = readSteps(definition);
  const firstStepId = id(definition?.firstStepId) ?? '';

  if (steps.length === 0) {
    return {
      isEmpty: true,
      width: 0,
      height: 0,
      stepCount: 0,
      firstStepId,
      hasFirstStep: false,
      steps: [],
      links: [],
      flagged: [],
    };
  }

  const byId = new Map(steps.map(step => [step.id, step]));
  const hasFirstStep = byId.has(firstStepId);

  // ── links, merged by the pair of steps they join ─────────────────────────────────
  // Two options of one choose that lead to the same step are ONE arrow with two labels.
  // Drawn separately they would be the same polyline twice, with the second label sitting
  // on top of the first — which is how "yes" and "no" ended up looking like one branch.
  const broken = new Set();
  const merged = new Map();
  steps.forEach(step => {
    branchesOf(step, lang).forEach(branch => {
      if (!byId.has(branch.to)) {
        broken.add(step.id);
        return;
      }
      // A pair of ids, unambiguously: joined with a separator this would be the one bug
      // where a step called `a` linking to `b|c` collides with `a|b` linking to `c`.
      const key = JSON.stringify([step.id, branch.to]);
      const existing = merged.get(key);
      if (existing) {
        existing.parts.push({ kind: branch.kind, label: branch.label });
        return;
      }
      merged.set(key, {
        key,
        from: step.id,
        to: branch.to,
        parts: [{ kind: branch.kind, label: branch.label }],
        isBack: false,
      });
    });
  });
  const links = [...merged.values()];

  const { walk, visitOrder } = classifyLinks(steps, links);
  if (hasFirstStep) walk(firstStepId);
  steps.forEach(step => walk(step.id));

  const forward = links.filter(link => !link.isBack);
  const rank = rankSteps(steps, forward);
  const reached = reachableFrom(firstStepId, steps, links);

  // ── columns ──────────────────────────────────────────────────────────────────────
  // Within a row, steps sit under the average column of the steps that lead to them. One
  // downward pass, because every forward link goes strictly down a row (longest-path
  // ranking guarantees it), so every parent already has a column by the time its children
  // are placed. It is not optimal crossing-wise and does not need to be: a task is at most
  // 30 steps and mostly a chain.
  const parentsOf = new Map(steps.map(step => [step.id, []]));
  forward.forEach(link => parentsOf.get(link.to)?.push(link.from));

  const rows = new Map();
  steps.forEach(step => {
    const row = rank.get(step.id) ?? 0;
    if (!rows.has(row)) rows.set(row, []);
    rows.get(row).push(step.id);
  });

  const orderKey = stepId => visitOrder.get(stepId) ?? Number.MAX_SAFE_INTEGER;
  const column = new Map();
  const rowNumbers = [...rows.keys()].sort((a, b) => a - b);
  rowNumbers.forEach(row => {
    const members = rows.get(row);
    members.sort((a, b) => {
      const centre = stepId => {
        const parents = (parentsOf.get(stepId) ?? [])
          .map(parent => column.get(parent))
          .filter(value => value !== undefined);
        if (parents.length === 0) return Number.MAX_SAFE_INTEGER;
        return parents.reduce((sum, value) => sum + value, 0) / parents.length;
      };
      const byCentre = centre(a) - centre(b);
      if (byCentre !== 0) return byCentre;
      const byVisit = orderKey(a) - orderKey(b);
      return byVisit !== 0 ? byVisit : a.localeCompare(b);
    });
    members.forEach((stepId, index) => column.set(stepId, index));
  });

  const widestRow = Math.max(...rowNumbers.map(row => rows.get(row).length));
  const boxesWidth = widestRow * BOX_W + (widestRow - 1) * GAP_X;

  // ── routing lanes ────────────────────────────────────────────────────────────────
  // A link that skips a row cannot run straight down the middle: between its two ends sit
  // other boxes, and a line through them is unreadable. Those go out to a lane at the side
  // — skips ahead down the start edge, loops back down the end edge — one lane each, with
  // the longest span outermost so a long loop never crosses a short one.
  const span = link => (rank.get(link.to) ?? 0) - (rank.get(link.from) ?? 0);
  const bySpan = (a, b) => {
    const difference = Math.abs(span(b)) - Math.abs(span(a));
    return difference !== 0 ? difference : a.key.localeCompare(b.key);
  };
  const skips = forward.filter(link => span(link) > 1).sort(bySpan);
  const loops = links.filter(link => link.isBack).sort(bySpan);

  const originX = PAD + skips.length * LANE_GAP;
  const boxesEnd = originX + boxesWidth;
  const width = boxesEnd + loops.length * LANE_GAP + PAD;
  const height =
    PAD_TOP +
    rowNumbers.length * BOX_H +
    (rowNumbers.length - 1) * GAP_Y +
    PAD_BOTTOM;

  const laneX = new Map();
  skips.forEach((link, index) => laneX.set(link.key, PAD + index * LANE_GAP));
  loops.forEach((link, index) =>
    laneX.set(link.key, boxesEnd + (loops.length - index) * LANE_GAP)
  );

  // ── the boxes ────────────────────────────────────────────────────────────────────
  const leaves = new Set(steps.map(step => step.id));
  links.forEach(link => leaves.delete(link.from));

  const placed = steps.map(step => {
    const row = rank.get(step.id) ?? 0;
    const col = column.get(step.id) ?? 0;
    const members = rows.get(row).length;
    const rowWidth = members * BOX_W + (members - 1) * GAP_X;
    const x = originX + (boxesWidth - rowWidth) / 2 + col * (BOX_W + GAP_X);
    const y = PAD_TOP + row * (BOX_H + GAP_Y);
    const isFirst = step.id === firstStepId;
    const isOrphan = !reached.has(step.id);
    // A step nothing follows is only a problem when the engine would not have stopped
    // there anyway: `end` and `handoff` are SUPPOSED to be the last thing that happens.
    const isDeadEnd =
      leaves.has(step.id) &&
      !TERMINAL_TYPES.has(step.type) &&
      step.type !== 'unknown';
    const isBroken = broken.has(step.id);
    return {
      id: step.id,
      type: step.type,
      name: step.name,
      nameLines: wrapName(step.name),
      x,
      y,
      w: BOX_W,
      h: BOX_H,
      row,
      col,
      isFirst,
      isOrphan,
      isDeadEnd,
      isBroken,
      tone: toneOf({ isBroken, isOrphan, isDeadEnd, isFirst }),
    };
  });
  const box = new Map(placed.map(step => [step.id, step]));

  // ── the lines ────────────────────────────────────────────────────────────────────
  const drawn = links.map(link => {
    const from = box.get(link.from);
    const to = box.get(link.to);
    const lane = laneX.get(link.key);
    const parts = link.parts.slice(0, MAX_LABEL_PARTS);
    const extra = link.parts.length - parts.length;
    const shared = {
      key: link.key,
      from: link.from,
      to: link.to,
      parts,
      extra,
    };

    /**
     * A link that goes out to a lane, whichever side the lane is on.
     *
     * SIX SEGMENTS, AND EVERY ONE OF THEM IS IN EMPTY SPACE. The first version left the
     * source's side edge and entered the target's side edge at their vertical centres, which
     * looked tidier and was wrong: "start again" looping from the confirmation back to the
     * studio question ran a horizontal line across the middle of the row the studio question
     * is in — straight through the box beside it. A horizontal run is only ever safe in a
     * gutter, so both of them are: down out of the source into the gutter below it, along the
     * lane, then along the gutter above the target and down into its top edge. The lane
     * itself is outside the columns entirely, so the vertical run cannot cross anything.
     *
     * A step that loops straight back to itself falls out of this with no special case: the
     * two horizontal runs are at the same x, and the path is a bracket around its own side.
     */
    if (lane !== undefined) {
      const exitX = from.x + from.w / 2;
      const exitY = from.y + from.h;
      const outY = exitY + LANE_TURN_OUT;
      const enterX = to.x + to.w / 2;
      const inY = to.y - LANE_TURN_IN;
      const startSide = lane < from.x;
      return {
        ...shared,
        isBack: Boolean(link.isBack),
        path: `M ${exitX} ${exitY} L ${exitX} ${outY} L ${lane} ${outY} L ${lane} ${inY} L ${enterX} ${inY} L ${enterX} ${to.y - ARROW}`,
        arrow: arrowPoints(enterX, to.y),
        // Beside the step it leaves, on the side it leaves towards — the label answers
        // "which way out of THIS step", so it belongs at the start and not out in the lane.
        labelX: startSide ? exitX - 8 : exitX + 8,
        labelY: outY - 5,
        labelAnchor: startSide ? 'end' : 'start',
      };
    }

    const startX = from.x + from.w / 2;
    const startY = from.y + from.h;
    const endX = to.x + to.w / 2;
    const endY = to.y;
    if (startX === endX) {
      return {
        ...shared,
        isBack: false,
        path: `M ${startX} ${startY} L ${endX} ${endY - ARROW}`,
        arrow: arrowPoints(endX, endY),
        labelX: startX + 8,
        // NOT the midpoint of the line, which is where the elbow beside it turns.
        labelY: startY + STRAIGHT_LABEL,
        labelAnchor: 'start',
      };
    }
    // The elbow turns in the gutter directly below the step it leaves, so the horizontal
    // run is always in empty space between two rows rather than across a box.
    const turnY = startY + TURN_Y;
    return {
      ...shared,
      isBack: false,
      path: `M ${startX} ${startY} L ${startX} ${turnY} L ${endX} ${turnY} L ${endX} ${endY - ARROW}`,
      arrow: arrowPoints(endX, endY),
      labelX: (startX + endX) / 2,
      labelY: turnY - 5,
      labelAnchor: 'middle',
    };
  });

  /**
   * What the owner is asked to look at, in reading order rather than in graph order — the
   * map shows her WHERE each problem is, this list makes sure she knows there is one at all
   * without having to scan sixteen boxes for a different border colour.
   */
  const flagged = placed
    .filter(step => step.tone !== 'plain' && step.tone !== 'first')
    .sort((a, b) => a.row - b.row || a.col - b.col);

  return {
    isEmpty: false,
    width,
    height,
    stepCount: steps.length,
    firstStepId,
    hasFirstStep,
    steps: placed,
    links: drawn,
    flagged,
  };
};
